import Link from 'next/link';
import emojiFlags from 'emoji-flags';
import { fraunces } from '../Landing/fonts';
import { Stamp } from '../Landing/ui';
import FutureImage from '../FutureImage';
import imageCrop from 'helpers/imageCrop';
import PhotoCredit from './PhotoCredit';

export function flagSrc(countryCode) {
  if (!countryCode || !emojiFlags.countryCode(countryCode.toUpperCase()))
    return null;
  return `/flags/${countryCode.toLowerCase()}.svg`;
}

// A destination is a whole region, not a pin — one decimal of its bbox
// center is the honest level of precision for the mono artifact line.
export function bboxCenterLine(bbox) {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const lng = (Number(bbox[0]) + Number(bbox[2])) / 2;
  const lat = (Number(bbox[1]) + Number(bbox[3])) / 2;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return `⌖ ${Math.abs(lat).toFixed(1)}° ${lat >= 0 ? 'N' : 'S'} · ${Math.abs(
    lng,
  ).toFixed(1)}° ${lng >= 0 ? 'E' : 'W'}`;
}

// The destination hero: one giant matted postcard — the landing page's
// Vang Vieng field-notes card scaled up to a guidebook cover. The photo is
// the LCP element; the white mat is a photographic print, so it stays light
// in dark mode. The caption strip carries the legally required photo
// credit (PhotoCredit) and the team-only edit pencil.
export default function FieldNotesPlate({
  image,
  blur,
  alt,
  title,
  kicker,
  subtitle,
  breadcrumbs,
  countryCode,
  bbox,
  capitalCity,
  topicStamp,
  captionLeft,
  locationDetails,
  editButton,
  compact,
}) {
  const flag = flagSrc(countryCode);
  const coords = bboxCenterLine(bbox);
  const isUnsplash = Boolean(locationDetails && locationDetails.unsplashUser);
  // Unsplash wants its CDN hot-linked (and already carries sizing params);
  // everything else goes through our proxy exactly once via imageCrop.
  const src = image
    ? isUnsplash
      ? image.replace(/&fm=jpg/, '&fm=webp')
      : imageCrop(image, 2000)
    : null;

  const metaParts = [];
  if (coords) metaParts.push(coords);
  if (capitalCity) metaParts.push(`CAPITAL: ${capitalCity.toUpperCase()}`);

  return (
    <div className="tw-mx-auto tw-w-[min(96vw,80rem)] tw-pt-3 sm:tw-pt-6">
      <figure className="tw-relative tw-m-0 tw-rounded-xl tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-1.5 tw-pb-0 sm:tw-p-3 sm:tw-pb-0 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)] dark:tw-shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
        <div
          className={`tw-relative tw-overflow-hidden tw-rounded-lg ${
            compact
              ? 'tw-aspect-[4/3] sm:tw-aspect-[16/9] lg:tw-aspect-[21/9]'
              : 'tw-aspect-[4/5] sm:tw-aspect-[16/10] lg:tw-aspect-[2/1]'
          }`}
        >
          {src ? (
            <>
              <FutureImage
                src={src}
                alt={alt || title}
                fill
                priority
                fetchPriority="high"
                unoptimized
                sizes="(max-width: 1320px) 96vw, 1280px"
                className="tw-object-cover"
                placeholder={blur ? 'blur' : undefined}
                blurDataURL={blur}
              />
              {/* Scrim for the title block — photo stays bright up top */}
              <div
                aria-hidden="true"
                className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-[rgba(10,18,15,0.72)] tw-via-[rgba(10,18,15,0.18)] tw-to-[rgba(10,18,15,0.05)]"
              />
            </>
          ) : (
            // Topics without a photo: a quiet paper plate instead.
            <div className="tw-absolute tw-inset-0 tw-bg-[#F6F3E9] dark:tw-bg-[#E9E2D2]">
              <svg
                aria-hidden="true"
                className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-text-[#1D2D28] tw-opacity-[0.05]"
                preserveAspectRatio="xMidYMid slice"
                viewBox="0 0 800 400"
              >
                <g fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M-40 60c120-45 240 35 360-5s215-60 360-15 160 55 160 55" />
                  <path d="M-40 105c120-45 240 35 360-5s215-60 360-15 160 55 160 55" />
                  <path d="M-40 330c135-60 255 45 375 0s200-75 345-20 160 60 160 60" />
                </g>
              </svg>
            </div>
          )}

          {/* Text block pinned to the lower-left of the photo */}
          <div
            className={`tw-absolute tw-inset-x-0 tw-bottom-0 tw-p-4 sm:tw-p-8 lg:tw-p-10 ${
              src ? '[text-shadow:0_1px_12px_rgba(0,0,0,0.45)]' : ''
            }`}
          >
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav
                aria-label="Breadcrumb"
                className={`tw-mb-3 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.22em] ${
                  src ? 'tw-text-[rgba(255,255,255,0.75)]' : 'tw-text-[#8A958F]'
                }`}
              >
                {breadcrumbs.map((crumb, i) => (
                  <span key={crumb.href}>
                    {i > 0 && <span aria-hidden="true"> → </span>}
                    <Link
                      href={crumb.href}
                      prefetch={false}
                      className={`tw-no-underline ${
                        src
                          ? 'tw-text-[rgba(255,255,255,0.75)] hover:tw-text-white'
                          : 'tw-text-[#8A958F] hover:tw-text-primary-700'
                      }`}
                    >
                      {crumb.name.toUpperCase()}
                    </Link>
                  </span>
                ))}
              </nav>
            )}

            {topicStamp && (
              <div className="tw-mb-4">
                <span
                  className={`tw-inline-block -tw-rotate-2 tw-rounded-md tw-border-2 tw-border-solid tw-px-3.5 tw-py-1.5 tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.3em] ${
                    src
                      ? 'tw-border-[rgba(252,211,77,0.9)] tw-text-amber-300'
                      : 'tw-border-amber-600/70 tw-text-amber-700'
                  }`}
                >
                  {topicStamp}
                </span>
              </div>
            )}

            {(flag || kicker) && (
              <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
                {flag && (
                  <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(255,255,255,0.88)] tw-px-2 tw-py-1">
                    <img
                      src={flag}
                      alt=""
                      aria-hidden="true"
                      width={16}
                      height={16}
                      loading="eager"
                      className="tw-h-4 tw-w-4"
                    />
                  </span>
                )}
                {kicker && (
                  <span
                    className={`tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] ${
                      src ? 'tw-text-primary-200' : 'tw-text-primary-700'
                    }`}
                  >
                    {kicker}
                  </span>
                )}
              </div>
            )}

            <h1
              className={`${
                fraunces.className
              } tw-m-0 tw-font-semibold tw-tracking-tight ${
                src ? 'tw-text-white' : 'tw-text-[#1D2D28]'
              } ${
                compact
                  ? 'tw-text-3xl sm:tw-text-5xl tw-leading-[1.05]'
                  : 'tw-text-4xl sm:tw-text-6xl lg:tw-text-7xl tw-leading-[1.02]'
              }`}
            >
              {title}
            </h1>

            {subtitle && (
              <p
                className={`tw-mb-0 tw-mt-3 tw-max-w-2xl tw-text-base sm:tw-text-lg tw-leading-relaxed ${
                  src ? 'tw-text-[rgba(255,255,255,0.85)]' : 'tw-text-[#5F6F69]'
                }`}
              >
                {subtitle}
              </p>
            )}

            {metaParts.length > 0 && (
              <p
                className={`tw-m-0 tw-mt-4 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.22em] ${
                  src ? 'tw-text-[rgba(255,255,255,0.7)]' : 'tw-text-[#8A958F]'
                }`}
              >
                {metaParts.join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Caption strip on the mat — the postcard's handwritten edge */}
        <figcaption className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-6 tw-gap-y-0.5 tw-px-1.5 tw-py-2.5 sm:tw-px-2 sm:tw-py-3 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.18em] tw-text-[#5F6F69] [&::after]:tw-hidden">
          <span className="tw-uppercase">{captionLeft}</span>
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-right">
            <PhotoCredit locationDetails={locationDetails} />
            {editButton}
          </span>
        </figcaption>

        <Stamp
          className="tw-absolute -tw-right-4 -tw-top-7 tw-hidden tw-h-24 tw-w-24 tw-rotate-12 tw-text-amber-600/90 dark:tw-text-amber-500/90 sm:tw-block"
          text="TRAVELFEED · FIELD NOTES · "
        />
      </figure>
    </div>
  );
}
